import { EditorLanguageContext } from '@app/components/cms/context/EditorLanguageContext';
import { Copy, Edit, Link, Remove, Settings, Visibility, VisibilityCrossed } from '@app/components/various/Icons';
import { unpublishPageRequest } from '@app/ducks';
import { useDeletePage } from '@app/services/hooks/pages/useDeletePage';
import { composeEditorPreviewPath } from '@app/utils/composeEditorPreviewPath';
import { isDefined } from '@app/utils/is';
import { Cms, Dropdown, Id } from '@typings';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { useConfirmationGuard } from '../useConfirmationGuard';
import { useModalVisibility } from '../useModalVisibility';

interface Props {
  page?: Cms.Page;
  setEditingPage: (id: Id) => void;
  openSharePage: (id: Id) => void;
  duplicatePage: ({ pageId }: { pageId: Id }) => void;
}

export const usePageOptions = ({ page, setEditingPage, openSharePage, duplicatePage }: Props): Dropdown.MenuItem[] => {
  const { t } = useTranslation(['confirmationConfig', 'common', 'cms']);
  const { editorLanguage } = React.useContext(EditorLanguageContext);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const confirmationGuard = useConfirmationGuard();
  const { showModal: showSettingsModal } = useModalVisibility('EditPageFormModal');

  const { mutate: deletePage } = useDeletePage();

  const openPageSettings = (pageId: string) => {
    showSettingsModal();
    setEditingPage(pageId);
  };

  const handleDeletePage = confirmationGuard((pageId: Id) => ({
    ...t('confirmationConfig:delete_page', { returnObjects: true }),
    okText: t('confirmationConfig:delete_page.title'),
    onOk: () => deletePage({ pageId }),
  }));

  if (!isDefined(page)) {
    return [];
  }

  const { id } = page;

  return [
    {
      icon: Edit,
      key: 'edit',
      label: t('cms:edit_content'),
      onClick: () => navigate(composeEditorPreviewPath({ lang: editorLanguage, pageId: id, pathType: 'editor' })),
    },
    {
      icon: Visibility,
      key: 'preview',
      label: t('cms:preview_page'),
      onClick: () => window.open(composeEditorPreviewPath({ lang: editorLanguage, pageId: id, pathType: 'preview' }), '_newtab'),
    },
    {
      icon: Settings,
      key: 'setting',
      label: t('cms:page_settings'),
      onClick: () => openPageSettings(id),
    },
    {
      icon: Copy,
      key: 'duplicate',
      label: t('cms:duplicate_page'),
      onClick: () => duplicatePage({ pageId: id }),
    },
    {
      icon: VisibilityCrossed,
      isDisabled: page.status === 'unpublished',
      key: 'unpublish',
      label: t('cms:unpublish'),
      onClick: () => dispatch(unpublishPageRequest({ id, languages: page.availableLanguages ?? [] })),
    },
    {
      icon: Link,
      isDisabled: page.status === 'unpublished',
      key: 'share',
      label: t('cms:get_shareable_link'),
      onClick: () => openSharePage(id),
    },
    {
      icon: Remove,
      key: 'delete',
      label: t('cms:delete_page'),
      onClick: () => handleDeletePage(id),
    },
  ];
};
